import React, { useRef } from "react";
import clsx from "clsx";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";

import {
    FileUpload as UIKitFileUpload,
    fileUploadTestIds as uiKitFileUploadTestIds,
} from "@proda-ai/ui-kit";
import FileExtractionError from "@/ui/organisms/FileExtraction/FileExtractionError";

export type FileUploadProps = Omit<BoxProps, "children"> & {
    error: {
        errorMessage: string;
        isNetworkError?: boolean;
    } | null;
    selectedFileName?: string | null | undefined;
    InnerBoxProps?: React.ComponentProps<typeof UIKitFileUpload>;
    resetFileUploadState?: () => void;
    handleFileDrop: (e: React.DragEvent<HTMLDivElement>) => void;
    handleFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onClose?: () => void;
    onLlmReExtract?: () => void;
};

export const testIds = {
    main: uiKitFileUploadTestIds.main,
    fileInput: "FileUpload-fileInput",
    fileInputButton: uiKitFileUploadTestIds.button,
    heading: uiKitFileUploadTestIds.heading,
};

const FileUpload = (props: FileUploadProps) => {
    const {
        sx,
        className,
        error,
        selectedFileName,
        InnerBoxProps,
        resetFileUploadState,
        handleFileDrop,
        handleFileInput,
        onClose,
        onLlmReExtract,
        ...otherProps
    } = props;

    const { sx: innerSx, ...innerOtherProps } = InnerBoxProps || {};

    const fileInputRef = useRef<HTMLInputElement>(null);

    const heading = `Drag and drop a PDF or Excel file to upload`;

    const handleButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    if (error !== null && error !== undefined) {
        return (
            <FileExtractionError
                error={error}
                selectedFileName={selectedFileName}
                onClose={onClose || (() => {})}
                sx={sx}
                className={className}
                resetFileUploadState={resetFileUploadState}
                onLlmReExtract={onLlmReExtract}
                {...otherProps}
            />
        );
    }

    return (
        <Box
            sx={{
                padding: "32px",
                ...sx,
            }}
            {...otherProps}
        >
            <UIKitFileUpload
                sx={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "32px",
                    boxSizing: "border-box",
                    ...innerSx,
                }}
                className={clsx([testIds.main, className])}
                buttonLabel="Choose file"
                heading={heading}
                caption="Maximum size 50 MB"
                handleClick={handleButtonClick}
                onDrop={handleFileDrop}
                {...innerOtherProps}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileInput}
                    hidden
                    id="fileInput"
                    data-testid={testIds.fileInput}
                />
            </UIKitFileUpload>
        </Box>
    );
};

export default FileUpload;
